import React from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import type { CodeToName, InstallablesByType, WebServerType } from './types';
import './HostingPackageTypeForm.css';

export interface Step3WebAppsProps {
  serverType: WebServerType;
  cgiOrderByType: InstallablesByType;
  cgiToName: CodeToName;
  /** Form-field name for the multi-value installables (e.g. `installables${id}` or `installables0`). */
  installablesFieldName: string;
  installables: Set<string>;
  installAutomatically: string;
  onToggleInstallable: (code: string, on: boolean) => void;
  onInstallAutomaticallyChange: (value: string) => void;
}

export const Step3WebApps: React.FC<Step3WebAppsProps> = ({
  serverType,
  cgiOrderByType,
  cgiToName,
  installablesFieldName,
  installables,
  installAutomatically,
  onToggleInstallable,
  onInstallAutomaticallyChange,
}) => {
  /* v8 ignore start -- `?? []` fallback for partial backend data; always present in practice */
  const installableCodes = cgiOrderByType[serverType] ?? [];
  /* v8 ignore stop */
  return (
    <>
      <fieldset className="hi-ui-hosting-package-type-form__category hi-ui-hosting-package-type-form__category--grid">
        <div className="hi-ui-hosting-package-type-form__category-legend">
          <Text variant="heading-3" inline>
            Installable applications
          </Text>
          <Text variant="body" className="hi-ui-hosting-package-type-form__category-subtext">
            Select the apps you want users on this package to be able to install on their account
            via the control panel
          </Text>
        </div>
        {installableCodes.map((code) => {
          if (!cgiToName[code]) return null;
          return (
            <Checkbox
              key={code}
              name={installablesFieldName}
              value={code}
              checked={installables.has(code)}
              onChange={(e) => onToggleInstallable(code, e.target.checked)}
              label={cgiToName[code]}
            />
          );
        })}
      </fieldset>

      <fieldset className="hi-ui-hosting-package-type-form__category hi-ui-hosting-package-type-form__category--block">
        <div className="hi-ui-hosting-package-type-form__category-legend">
          <Text variant="heading-3" inline>
            Default application install
          </Text>
          <Text variant="body" className="hi-ui-hosting-package-type-form__category-subtext">
            Automatically install the selected app for new accounts on this package
          </Text>
        </div>
        <Select
          name={`${installablesFieldName}-autoinstall`}
          value={installAutomatically}
          onChange={(e) => onInstallAutomaticallyChange(e.target.value)}
          fullWidth
          aria-label="Install automatically"
        >
          <option value="">— None —</option>
          {installableCodes.map((code) => {
            if (!cgiToName[code]) return null;
            const optionValue = `-autoinstall=${code}`;
            return (
              <option key={optionValue} value={optionValue}>
                {cgiToName[code]}
              </option>
            );
          })}
        </Select>
      </fieldset>
    </>
  );
};
